import React, { FC, ReactElement, useCallback, useMemo, useState } from 'react';
import { CopyToClipboard as ExternalCopyToClipboard } from 'react-copy-to-clipboard';
import Tooltip, { Placement } from '@clds/tooltip';

export interface CopyToClipboardProps {
  /** text to show when hovering over the component */
  tooltipBeforeText?: string;
  /** text to show when copy is invoked (if not defined using `tooltipBeforeText`) */
  tooltipAfterText?: string;
  tooltipPlacement?: Placement;
  /** text which will be copied to clipboard */
  copiedValue: string;
  onCopy?: () => void;
  children: ReactElement;
}

export const CopyToClipboard: FC<React.PropsWithChildren<CopyToClipboardProps>> = ({
  onCopy,
  tooltipBeforeText,
  tooltipAfterText,
  copiedValue,
  children,
  tooltipPlacement,
}) => {
  const [copied, setCopied] = useState(false);

  const onCopyHandler = useCallback(() => {
    setCopied(true);
    onCopy?.();
  }, [setCopied, onCopy]);

  const onTooltipOpenHandler = useCallback(
    (visible: boolean) => {
      if (visible) {
        setCopied(false);
      }
    },
    [setCopied]
  );

  const title = useMemo(() => (copied ? tooltipAfterText || tooltipBeforeText : tooltipBeforeText), [copied, tooltipAfterText, tooltipBeforeText]);

  return (
    <ExternalCopyToClipboard text={copiedValue} onCopy={onCopyHandler}>
      <Tooltip title={title} onVisibleChange={onTooltipOpenHandler} placement={tooltipPlacement}>
        {children}
      </Tooltip>
    </ExternalCopyToClipboard>
  );
};
